/* eslint-disable no-console */
import { getScrollOptions, getElementPosition } from './utils';
import { focusElement, focusPageHeader } from '../ui/focus';
import { ERROR_ELEMENTS, SCROLL_ELEMENT_SUFFIX } from '../constants';
import environment from '../environment';
import {
  cleanupErrorAnnotations as cleanupErrorAnnotationsInternal,
  DEFAULT_SCAFFOLD_AND_FOCUS_FORM_ERRORS,
  findFocusTarget,
  isSupportedVaElement,
  scaffoldErrorsFromSelectors,
} from './error-scaffolding';

export const cleanupErrorAnnotations = cleanupErrorAnnotationsInternal;

// Let the form system control scroll behavior
window.history.scrollRestoration = 'manual';

// Error message selectors used for alert role removal before focus
const ERROR_MESSAGE_SELECTORS = [
  '#error-message',
  '#input-error-message',
  '#radio-error-message',
  '#checkbox-error-message',
];

// Hosts that can contain an errored child nested inside their shadow DOM while
// neither the host nor the intermediate wrappers reflect an `error` attribute.
// For example, an encrypted va-file-input renders a password va-text-input in
// its shadow root, and a va-file-input-multiple renders va-file-input children
// in its shadow root (so the errored password input sits two shadow boundaries
// deep). When the user submits without entering a password, that nested input is
// the only element carrying the error. document.querySelector can't pierce
// shadow DOM, so these hosts' shadow roots are searched (and observed) directly.
const NESTED_ERROR_HOST_SELECTOR = 'va-file-input, va-file-input-multiple';

// The attribute-based subset of ERROR_ELEMENTS. Web components signal errors by
// reflecting an attribute, so when drilling into shadow DOM we match only these.
// The class-based entries (.usa-input-error, .input-error-date) belong to legacy
// light-DOM fields and never appear inside a web component's shadow root —
// matching them there risks focusing a component's internal error-message markup
// instead of the nested field.
const SHADOW_ERROR_SELECTOR = ERROR_ELEMENTS.filter(sel =>
  sel.startsWith('['),
).join(',');

/**
 * Scroll element to top of the page
 * @param {String|Number|Element} el - selector, id, name, class name, number,
 *  or DOM element to position
 * @param {ScrollOptions} scrollOptions - settings & overrides
 * @returns Promise
 */
export const scrollTo = async (el, scrollOptions) => {
  const options = getScrollOptions(scrollOptions);
  const scroll = resolve => {
    const top =
      (options.top || getElementPosition(el, options.root)) +
      (options.offset || 0);
    // Scroll to calculated position
    window.scrollTo({
      top: Math.round(top),
      left: options.left || 0,
      behavior: options.behavior,
    });
    resolve();
  };
  return new Promise(resolve =>
    options.delay
      ? setTimeout(() => {
          scroll(resolve);
        }, options.delay)
      : scroll(resolve),
  );
};

/**
 * scrollTo alias
 * Scroll element to top of the page
 * @param {String|Number|Element} el - selector, id, name, class name, number,
 *  or DOM element to position
 * @param {ScrollOptions} scrollOptions - settings & overrides
 * @returns Promise
 */
export const scrollToElement = async (el, scrollOptions) =>
  scrollTo(el, scrollOptions);

/**
 * Scroll to top of the page
 * @param {String|Number|Element} [position='topScrollElement'] - top scroll element, or
 *  selector, id, name, class name, number, or DOM element to position
 * @param {ScrollOptions} [scrollOptions] - settings & overrides
 * @returns {Promise<void>}
 */
export const scrollToTop = async (
  position = `top${SCROLL_ELEMENT_SUFFIX}`,
  scrollOptions,
) => scrollTo(position, scrollOptions);

/**
 * scrollToFirstError options
 * @typedef scrollToFirstErrorOptions
 * @type {Object}
 * @property {Boolean} focusOnAlertRole=false - When a web component is targetted, find
 *  and focus on the element with role=alert to ensure screen readers are
 *  reading out the correct content; it's set default to false while we perform
 *  further accessibility evaluation
 * @property {Boolean} scaffoldAndFocusFormErrors - When a supported VA web component has an
 *  error, find and focus on the first input element inside its shadow DOM. Set to false
 *  to opt out and focus on the error element itself instead.
 */
/**
 * Find first error and scroll it to the top of the page, then focus on it
 * @param {ScrollOptions & scrollToFirstErrorOptions} options
 */
export const scrollToFirstError = async (options = {}) => {
  return new Promise(resolve => {
    const {
      focusOnAlertRole = false,
      scaffoldAndFocusFormErrors = DEFAULT_SCAFFOLD_AND_FOCUS_FORM_ERRORS,
      errorContext,
    } = options;
    const selectors = ERROR_ELEMENTS.join(',');
    const timeout = 500;
    const observerConfig = {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['error'],
    };
    const rootEl = document.querySelector('#react-root') || document.body;
    let fallbackTimer;
    let observer;

    const runCleanup = el => {
      if (!el) {
        // Suppress warning in test environments to avoid noisy output
        if (!window.Mocha) {
          console.warn('scrollToFirstError: Error element not found', el);
        }
        if (
          !window.Mocha &&
          !environment.isProduction() &&
          Array.isArray(errorContext) &&
          errorContext.length &&
          errorContext.some(err =>
            err?.stack?.includes('You must provide a response'),
          )
        ) {
          const fieldKeys = errorContext
            .filter(err => err?.stack?.includes('You must provide a response'))
            .map(err => err.stack.split(':')[0]);
          console.warn(
            `Schema validation error: The following fields are marked as required in your schema but do not exist in the page:\n${fieldKeys
              .map(key => `- "${key}"`)
              .join(
                '\n',
              )}\nCheck your required fields in the schema match the fields in your UI.`,
          );
        }
      }
      clearTimeout(fallbackTimer);
      observer?.disconnect();
      resolve();
    };

    const scrollAndFocus = el => {
      // check for any modals that would interfere with scrolling
      const isShadowRootModalOpen = Array.from(
        document.querySelectorAll('va-omb-info'),
      ).some(({ shadowRoot }) =>
        shadowRoot?.querySelector('va-modal[visible]:not([visible="false"])'),
      );
      const isModalOpen =
        document.body.classList.contains('modal-open') ||
        document.querySelector('va-modal[visible]:not([visible="false"])') ||
        isShadowRootModalOpen;

      // prevent page scroll if there is an open modal, as the error could
      // be within the modal itself
      if (!isModalOpen) {
        const position = getElementPosition(el);
        scrollTo(position - 10, options);

        if (focusOnAlertRole) {
          // Adding a delay so that the shadow DOM needs to render and attach
          // before we try to focus on the element; without the setTimeout,
          // focus ends up staying on the "Continue" button
          requestAnimationFrame(() => {
            focusElement('[role="alert"]', {}, el?.shadowRoot);
          });
        } else if (scaffoldAndFocusFormErrors && isSupportedVaElement(el)) {
          scaffoldErrorsFromSelectors(selectors);

          // Find and focus the appropriate input element
          // Temporarily remove alert role from error message elements to prevent competing announcements
          const focusTarget = findFocusTarget(el);
          if (focusTarget) {
            const errorElements = el?.shadowRoot?.querySelectorAll(
              ERROR_MESSAGE_SELECTORS.join(', '),
            );
            const originalAttributes = [];
            errorElements?.forEach(errorEl => {
              const role = errorEl.getAttribute('role');
              originalAttributes.push({ element: errorEl, role });
              errorEl.removeAttribute('role');
            });

            focusTarget.focus({ preventScroll: true });

            // 500ms timeout here is somewhat arbitrary but is intended to be long enough for focus to occur
            // and screen readers to announce the focused element before restoring alert roles to error message elements
            setTimeout(() => {
              originalAttributes.forEach(({ element, role }) => {
                if (role) element.setAttribute('role', role);
              });
            }, 500);
          }
        } else {
          focusElement(el);
        }
      }

      runCleanup(true);
    };

    /**
     * Finds the errored field nested inside a file-input host's shadow DOM, even
     * when neither the host nor the intermediate wrappers reflect an `error`
     * attribute. This covers encrypted file inputs: when the user clicks
     * "Continue" without entering a password, the nested password va-text-input
     * is the only element carrying the error, and it can sit one or more shadow
     * boundaries deep (va-file-input-multiple > va-file-input > va-text-input)
     * where document.querySelector can't reach it.
     *
     * Only file-input hosts are traversed (the sole components that nest an error
     * below a non-reflecting wrapper), so this relies on native querySelector
     * rather than walking every element on the page.
     *
     * @param {Document|ShadowRoot|null} root
     * @returns {Element|null}
     */
    const findNestedHostError = root => {
      if (!root) return null;
      // Prefer a more deeply nested host's error (e.g. a va-file-input inside a
      // va-file-input-multiple) so focus lands on the field, not the wrapper.
      for (const host of root.querySelectorAll(NESTED_ERROR_HOST_SELECTOR)) {
        const deeper = findNestedHostError(host.shadowRoot);
        if (deeper) return deeper;
      }

      return root.querySelector(SHADOW_ERROR_SELECTOR);
    };

    /**
     * Queries for error elements and scrolls to focus on them.
     * Prioritizes focusing on nested child error elements within shadow DOM,
     * falling back to the parent element if no child errors are found.
     */
    const queryForErrors = () => {
      // Fast path: the first error element in the light DOM.
      const match = document.querySelector(selectors);
      // The match may be a file-input wrapper whose real error is the field
      // nested in its shadow DOM; or, when nothing matched, the only error may be
      // a field nested inside a file-input host. Resolve to that field so focus
      // targets the offending input rather than a wrapper.
      const el =
        findNestedHostError(match?.shadowRoot) ||
        match ||
        findNestedHostError(document);
      if (el) {
        scrollAndFocus(el);
      }
    };

    // Watch for errors that appear after this function runs: either an `error`
    // attribute being reflected onto an existing web component, or new error
    // nodes being added to the DOM.
    observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes') {
          queryForErrors();
          return;
        }
        for (const node of Array.from(mutation.addedNodes)) {
          if (
            node.nodeType === Node.ELEMENT_NODE &&
            (node.matches?.(selectors) || node.querySelector?.(selectors))
          ) {
            queryForErrors();
            return;
          }
        }
      }
    });
    if (rootEl) observer.observe(rootEl, observerConfig);

    // A light-DOM observer can't see attribute changes inside shadow roots, and a
    // subtree observation does not cross nested shadow boundaries. So observe each
    // file-input host's shadow root (and any file-input hosts nested within it),
    // so an error reflected after this runs (e.g. the password field of an
    // encrypted va-file-input nested inside a va-file-input-multiple) re-triggers
    // the search.
    const observeHostShadowRoot = host => {
      const { shadowRoot } = host;
      if (!shadowRoot) return;
      observer.observe(shadowRoot, {
        attributes: true,
        attributeFilter: ['error'],
        subtree: true,
      });
      shadowRoot
        .querySelectorAll(NESTED_ERROR_HOST_SELECTOR)
        .forEach(observeHostShadowRoot);
    };
    document
      .querySelectorAll(NESTED_ERROR_HOST_SELECTOR)
      .forEach(observeHostShadowRoot);

    // don't let the observer run forever
    fallbackTimer = setTimeout(() => runCleanup(false), timeout);

    // run an initial check for any existing elements
    queryForErrors();
  });
};

export const scrollAndFocus = (target, options) =>
  new Promise(resolve => {
    if (target) {
      scrollTo(target, options);
      focusElement(target, { preventScroll: true });
    }
    resolve();
  });

/**
 * Custom focus - focuses on a page's H3 by default (unique header) if it exists
 * will fall back to the breadcrumb H2 (Step _ of _). This function is called
 * only if the formConfig includes a `useCustomScrollAndFocus: true`, then it
 * checks the page's `scrollAndFocusTarget` setting which is either a string or
 * function to allow for custom focus management, e.g. returning to a page after
 * editing a value to ensure focus is returned to the edit link
 * @param {String|Function} scrollAndFocusTarget - Custom focus target
 * @param {Number} pageIndex - index inside of a page array loop
 * @param {ScrollOptions} options - settings & overrides
 */
export const customScrollAndFocus = (
  scrollAndFocusTarget,
  pageIndex,
  options = {},
) =>
  new Promise(resolve => {
    setTimeout(() => {
      if (typeof scrollAndFocusTarget === 'string') {
        scrollAndFocus(document.querySelector(scrollAndFocusTarget));
      } else if (typeof scrollAndFocusTarget === 'function') {
        scrollAndFocusTarget(pageIndex);
      } else {
        scrollToTop(`top${SCROLL_ELEMENT_SUFFIX}`, options);
        // h3 should be a unique header on the page
        focusPageHeader(3);
      }
      resolve();
    }, options.delay || 150);
  });
